import React, { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { useI18n } from "../../i18n/I18nProvider";
import { formatMonthYear, noteDayLabel } from "../../lib/sampleWeek";

const CountUp = ({ value }) => {
  const reduced = useReducedMotion();
  const digits = String(value).replace(/[^\d.]/g, "");
  const numeric = digits === "" ? Number.NaN : Number(digits);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [shown, setShown] = useState(value);

  useEffect(() => {
    if (!Number.isFinite(numeric)) {
      setShown(value);
      return undefined;
    }
    if (reduced) {
      setShown(value);
      return undefined;
    }
    if (!inView) return undefined;
    setShown("0");
    const start = performance.now();
    let frame = 0;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / 600);
      const eased = 1 - (1 - progress) ** 3;
      setShown(String(Math.round(numeric * eased)));
      if (progress < 1) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [inView, numeric, reduced, value]);

  return <span ref={ref}>{shown}</span>;
};

const Studio = ({ embedded = false }) => {
  const { t, lang, dir } = useI18n();
  const reduced = useReducedMotion();
  const rows = t("studio.rows");
  const notes = t("studio.notes");
  const isRtl = dir === "rtl";
  const lineRef = useRef(null);
  const lineInView = useInView(lineRef, { once: true, amount: 0.3 });

  if (embedded) {
    return (
      <div className="grid gap-3">
        <div className="rounded-card border border-hairline bg-paper p-4 md:p-5">
          <p className="text-caption font-medium text-accent">{t("rows.rate.builder")}</p>
          <div className="mt-4 grid gap-3">
            <div className="flex items-center justify-between gap-3 rounded-nested border border-hairline px-3 py-2">
              <span className="text-caption text-mid-gray">{t("rows.rate.type")}</span>
              <span className="text-body font-semibold text-ink">{t("rows.rate.typeValue")}</span>
            </div>
            <div className="rounded-nested border border-hairline px-3 py-2">
              <p className="text-caption text-mid-gray">{t("rows.rate.titleLabel")}</p>
              <p className="mt-1 text-body font-semibold text-ink">{t("rows.rate.titleValue")}</p>
            </div>
            <div className="rate-amount flex items-center justify-between gap-3 rounded-nested px-3 py-2">
              <span className="text-caption">{t("rows.rate.amount")}</span>
              <span className="text-heading-sm font-semibold tabular-nums">
                <CountUp value={t("rows.rate.amountValue")} /> {t("studio.unit")}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 px-1">
              <span className="text-body text-ink">{t("rows.rate.produce")}</span>
              <span className="h-6 w-11 rounded-pill bg-accent p-0.5">
                <span className="ms-auto block h-5 w-5 rounded-full bg-white" />
              </span>
            </div>
          </div>
        </div>
        <div className="rounded-card border border-hairline bg-paper p-4 md:p-5">
          <p className="text-caption font-medium text-accent">{t("rows.rate.preview")}</p>
          <p className="mt-2 text-body font-semibold text-ink">{t("rows.rate.from")}</p>
          <p className="mt-1 text-caption text-mid-gray">{t("rows.rate.titleValue")}</p>
          <div className="mt-4 flex items-end justify-between gap-3">
            <div>
              <p className="text-caption text-mid-gray">{t("rows.rate.income")}</p>
              <p className="text-heading-sm font-semibold text-ink">
                <CountUp value={t("rows.rate.amountValue")} /> {t("studio.unit")}
              </p>
            </div>
            <p className="text-caption text-accent">{t("rows.rate.before")}</p>
          </div>
        </div>
      </div>
    );
  }

  const sheet = (
        <div className={`${embedded ? "" : "mt-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)]"} grid gap-6 rounded-card border border-hairline bg-paper p-6 md:p-8`}>
          <div>
            <div className="mb-6 flex items-baseline justify-between gap-4">
              <p className="text-body font-medium text-ink">{formatMonthYear(lang)}</p>
              <p className="text-caption text-mid-gray">{t("studio.unit")}</p>
            </div>
            <ul className="m-0 flex flex-col p-0">
              {rows.map((row) => (
                <li
                  key={row.type}
                  className={`flex list-none items-baseline gap-3 border-b border-hairline py-4 last:border-0 ${
                    row.featured ? "rounded-nested bg-lilac/60 px-2" : ""
                  }`}
                >
                  <span className="w-8 shrink-0 text-caption text-mid-gray">{row.n}</span>
                  <span
                    className={`min-w-0 flex-1 text-heading-sm font-semibold ${
                      row.featured ? "text-accent" : "text-ink"
                    }`}
                  >
                    {row.type}
                  </span>
                  <span className="shrink-0 text-heading-sm font-semibold tabular-nums text-ink">
                    <CountUp value={row.price} />
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div ref={lineRef}>
            <p className="mb-6 text-body font-medium text-ink">{t("studio.noteTitle")}</p>
            <ol className="relative m-0 flex flex-col p-0">
              <span
                aria-hidden="true"
                className={`absolute top-2 bottom-2 w-px origin-top bg-accent transition-transform duration-700 ${
                  isRtl ? "right-0" : "left-0"
                } ${lineInView && !reduced ? "scale-y-100" : "scale-y-0"}`}
                style={reduced ? { transform: "scaleY(1)" } : undefined}
              />
              {notes.map((note, index) => (
                <li
                  key={note.text}
                  className={`relative list-none py-4 ${isRtl ? "pr-6" : "pl-6"}`}
                >
                  <span
                    className={`absolute top-6 h-2 w-2 rounded-full ${
                      isRtl ? "right-[-3px]" : "left-[-3px]"
                    } ${index === 0 ? "bg-accent" : "bg-hairline"}`}
                  />
                  <p className="text-body text-mid-gray">
                    {noteDayLabel(t, lang, note.offset)}
                    <span className="mx-2 text-accent">·</span>
                    {note.when}
                  </p>
                  <p className="mt-1 text-body text-ink">{note.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
  );

  return sheet;
};

export default Studio;
